/**
 * Asset Storage Service
 * Handles image upload and management for Google Ads assets
 */

import { storage } from '../config/firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { openaiService } from './openaiService';

export const assetStorageService = {
  /**
   * Upload image to Firebase Storage
   */
  async uploadImage(
    file: File,
    orgId: string,
    campaignId: string
  ): Promise<string> {
    try {
      // Validate file
      if (!file.type.startsWith('image/')) {
        throw new Error('File must be an image');
      }

      if (file.size > 5 * 1024 * 1024) {
        throw new Error('Image must be less than 5MB');
      }

      // Create storage reference
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const storageRef = ref(storage, `google-ads-assets/${orgId}/${campaignId}/${fileName}`);

      // Upload file
      const snapshot = await uploadBytes(storageRef, file, {
        contentType: file.type,
        customMetadata: {
          orgId,
          campaignId,
          uploadedAt: new Date().toISOString()
        }
      });

      // Get download URL
      const url = await getDownloadURL(snapshot.ref);
      
      console.log(`✅ Image uploaded: ${fileName}`);
      return url;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  },

  /**
   * Upload image blob to Firebase Storage (for harvested images)
   */
  async uploadImageBlob(
    blob: Blob,
    orgId: string,
    fileName: string
  ): Promise<string> {
    try {
      // Validate blob
      if (blob.size > 5 * 1024 * 1024) {
        throw new Error('Image must be less than 5MB');
      }

      // Create storage reference
      const storageRef = ref(storage, `google-ads-assets/${orgId}/${fileName}`);

      // Upload blob
      const snapshot = await uploadBytes(storageRef, blob, {
        contentType: blob.type || 'image/jpeg',
        customMetadata: {
          orgId,
          uploadedAt: new Date().toISOString(),
          source: 'harvested'
        }
      });

      // Get download URL
      const url = await getDownloadURL(snapshot.ref);
      
      console.log(`✅ Image blob uploaded: ${fileName}`);
      return url;
    } catch (error) {
      console.error('Error uploading image blob:', error);
      throw error;
    }
  },

  /**
   * Delete image from storage
   */
  async deleteImage(imageUrl: string): Promise<void> {
    try {
      // Extract path from URL
      const urlObj = new URL(imageUrl);
      const pathMatch = urlObj.pathname.match(/\/o\/(.+)\?/);
      
      if (!pathMatch) {
        throw new Error('Invalid image URL');
      }

      const path = decodeURIComponent(pathMatch[1]);
      const storageRef = ref(storage, path);
      
      await deleteObject(storageRef);
      console.log(`✅ Image deleted: ${path}`);
    } catch (error) {
      console.error('Error deleting image:', error);
      throw error;
    }
  },

  /**
   * Generate marketing image using AI (DALL-E)
   */
  async generateImageWithAI(
    prompt: string,
    aspectRatio: '1792x1024' | '1024x1024' | '1024x1792' = '1792x1024'
  ): Promise<string> {
    try {
      console.log('🎨 Generating image with AI...');
      
      const enhancedPrompt = `Professional marketing image: ${prompt}
Style: Modern, clean, high-quality
Format: Product photography or illustration
Colors: Vibrant but professional
No text overlay`;

      const imageUrl = await openaiService.generateImage(enhancedPrompt, aspectRatio);
      
      console.log('✅ AI image generated');
      return imageUrl;
    } catch (error) {
      console.error('Error generating AI image:', error);
      throw error;
    }
  },

  /**
   * Validate image dimensions
   */
  async validateImageDimensions(
    file: File,
    requiredRatio?: number
  ): Promise<{ width: number; height: number; ratio: number; valid: boolean }> {
    return new Promise((resolve) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        const width = img.width;
        const height = img.height;
        const ratio = width / height;
        
        let valid = true;
        
        // Check minimum dimensions
        if (width < 300 || height < 300) {
          valid = false;
        }

        // Check ratio if specified
        if (requiredRatio) {
          const tolerance = 0.1;
          if (Math.abs(ratio - requiredRatio) > tolerance) {
            valid = false;
          }
        }

        URL.revokeObjectURL(objectUrl);
        resolve({ width, height, ratio, valid });
      };

      img.src = objectUrl;
    });
  },

  /**
   * Upload video to Firebase Storage
   */
  async uploadVideo(
    file: File,
    orgId: string,
    campaignId: string
  ): Promise<string> {
    try {
      // Validate file
      const validTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/avi'];
      if (!validTypes.includes(file.type)) {
        throw new Error('Video must be MP4, MOV, or AVI format');
      }

      if (file.size > 1024 * 1024 * 1024) {
        throw new Error('Video must be less than 1GB');
      }

      // Create storage reference
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const storageRef = ref(storage, `google-ads-videos/${orgId}/${campaignId}/${fileName}`);

      // Upload with progress tracking
      const snapshot = await uploadBytes(storageRef, file, {
        contentType: file.type,
        customMetadata: {
          orgId,
          campaignId,
          uploadedAt: new Date().toISOString()
        }
      });

      // Get download URL
      const url = await getDownloadURL(snapshot.ref);
      
      console.log(`✅ Video uploaded: ${fileName}`);
      return url;
    } catch (error) {
      console.error('Error uploading video:', error);
      throw error;
    }
  },

  /**
   * Validate video duration and format
   */
  async validateVideo(file: File): Promise<{
    duration: number;
    width: number;
    height: number;
    ratio: string;
    valid: boolean;
    error?: string;
  }> {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      const objectUrl = URL.createObjectURL(file);

      video.onloadedmetadata = () => {
        const duration = video.duration;
        const width = video.videoWidth;
        const height = video.videoHeight;
        const ratio = width / height;

        let ratioString = '16:9';
        if (Math.abs(ratio - 1) < 0.1) ratioString = '1:1';
        else if (Math.abs(ratio - 0.56) < 0.1) ratioString = '9:16';

        let valid = true;
        let error = '';

        // Google Ads video requirements
        if (duration < 6) {
          valid = false;
          error = 'Video must be at least 6 seconds';
        } else if (duration > 120) {
          valid = false;
          error = 'Video must be less than 120 seconds (2 minutes)';
        }

        URL.revokeObjectURL(objectUrl);
        resolve({ duration, width, height, ratio: ratioString, valid, error });
      };

      video.src = objectUrl;
    });
  },

  /**
   * Get stock/placeholder images for demo mode
   */
  getStockImages(): Array<{ url: string; ratio: string; description: string }> {
    return [
      {
        url: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=628',
        ratio: '1.91:1',
        description: 'Business Dashboard'
      },
      {
        url: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=1200',
        ratio: '1:1',
        description: 'Charts & Analytics'
      },
      {
        url: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=960&h=1200',
        ratio: '4:5',
        description: 'Team Collaboration'
      }
    ];
  },

  /**
   * Get stock videos for demo
   */
  getStockVideos(): Array<{ url: string; youtubeId?: string; description: string }> {
    return [
      {
        youtubeId: 'dQw4w9WgXcQ',
        description: 'Product Demo Video',
        url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ'
      },
      {
        youtubeId: 'dQw4w9WgXcQ',
        description: 'Customer Testimonial',
        url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ'
      }
    ];
  }
};

